import React, { forwardRef, useCallback, useMemo, useState } from 'react';
import Bubble from './Bubble';
import { useIdentity } from '../../hooks/useIdentity';
import DiffView from '../codedrop/DiffView';
import CodeComparePicker from '../codedrop/CodeComparePicker';
import './MessageList.css';

/**
 * Stateless list of bubbles. Anchor ref is forwarded so the owning ChatRegion
 * can call scrollIntoView on it. The anchor is a single sibling AFTER the map
 * — never inside it — so scrolling targets exactly one node.
 *
 * Props:
 *   - messages: array of normalized messages (see firestore.js::normalizeMessage)
 *   - selfId:   senderId to compare against for isSelf
 *
 * Filters out messages from senderIds present in the per-session block list
 * (F9.1). The filter is applied here rather than in ChatRegion so that
 * unread/scroll bookkeeping in the owner stays count-true for the unfiltered
 * stream — block changes do not retroactively un-read messages.
 *
 * F12.4 — Code Drop compare orchestration: when a CodeBubble fires
 * `onCompareRequest`, this component opens a `CodeComparePicker` over the
 * room's other code snippets (filtered to matching `lang`), and on pick
 * opens a `DiffView` against the source. Both modals are mounted in the
 * MessageList subtree so they survive bubble unmount/remount during snapshot
 * churn.
 */
const MessageList = forwardRef(function MessageList(
  { messages, selfId },
  anchorRef,
) {
  const { blocked, avatarSeed: selfAvatarSeed } = useIdentity();

  const visibleMessages = useMemo(() => {
    if (!blocked || blocked.length === 0) return messages;
    const blockSet = new Set(blocked);
    return messages.filter((m) => !blockSet.has(m.senderId));
  }, [messages, blocked]);

  // F12.4 — Code Drop compare flow state.
  // Source = the snippet the user invoked "Compare with…" from.
  // Picker is open while the user is choosing a target.
  // Diff target = the chosen target; when set, DiffView renders.
  const [compareSource, setCompareSource] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [diffTarget, setDiffTarget] = useState(null);

  // Snapshot of all visible code-attachment snippets — used as picker rows.
  // Pre-computed once per message change rather than per-Bubble.
  const codeSnippets = useMemo(() => {
    const out = [];
    for (const m of visibleMessages) {
      const a = m.attachment;
      if (a && a.kind === 'code' && typeof a.text === 'string') {
        out.push({
          id: m.id,
          lang: a.lang,
          title: a.title,
          text: a.text,
          senderName: m.senderName,
          sentAt: m.sentAt,
        });
      }
    }
    return out;
  }, [visibleMessages]);

  const handleCompareRequest = useCallback((snippet) => {
    setCompareSource(snippet);
    setDiffTarget(null);
    setPickerOpen(true);
  }, []);

  const handlePickerSelect = useCallback((snippet) => {
    setDiffTarget(snippet);
    setPickerOpen(false);
  }, []);

  const closePicker = useCallback(() => {
    setPickerOpen(false);
    // Keep `compareSource` around so the user can re-open the picker without
    // losing context — only fully clear when the diff view closes.
  }, []);

  const closeDiff = useCallback(() => {
    setDiffTarget(null);
    setCompareSource(null);
  }, []);

  // Picker candidates = same-lang snippets, excluding the source itself.
  const candidateSnippets = useMemo(() => {
    if (!compareSource) return [];
    return codeSnippets.filter(
      (s) =>
        s.lang === compareSource.lang &&
        // exclude the literal source row (matching by text+title is good
        // enough — two snippets identical in both are arguably the same
        // thing and not interesting to diff).
        !(s.text === compareSource.text && s.title === compareSource.title),
    );
  }, [codeSnippets, compareSource]);

  return (
    <div className="glacier-message-list" data-testid="message-list">
      {visibleMessages.map((m) => {
        const isSelf = m.senderId === selfId;
        // For own messages, override the stored avatarSeed with the live
        // useIdentity().avatarSeed so the user's reroll updates their past
        // bubbles too. Other senders keep their own stored seed (we never
        // retroactively rewrite messages for them — they own their identity).
        const seed = isSelf
          ? selfAvatarSeed
          : m.avatarSeed || m.senderId;
        return (
          <Bubble
            key={m.id}
            id={m.id}
            text={m.text}
            senderId={m.senderId}
            senderName={m.senderName}
            avatarSeed={seed}
            sentAt={m.sentAt}
            isSelf={isSelf}
            status={m.status || 'sent'}
            reactions={m.reactions}
            replyTo={m.replyTo}
            attachment={m.attachment}
            onCompareRequest={handleCompareRequest}
          />
        );
      })}
      <div
        ref={anchorRef}
        className="glacier-message-list__anchor"
        aria-hidden="true"
      />

      {/* F12.4 compare flow — modals live here so they survive bubble churn. */}
      <CodeComparePicker
        isOpen={pickerOpen}
        source={compareSource}
        snippets={candidateSnippets}
        onPick={handlePickerSelect}
        onClose={closePicker}
      />
      <DiffView
        isOpen={!!diffTarget}
        source={compareSource}
        target={diffTarget}
        onClose={closeDiff}
      />
    </div>
  );
});

export default MessageList;
